import api, { mailerServiceApi } from 'services/api';
import { GET, POST, PUT, DELETE, emailRoutes } from 'constants/api';
import urlJoin from 'url-join';
import { getEnv } from './env-service';

const MISSING_EMAIL_ID = 'Email id is missing';

const checkEmailId = emailId => {
  if (!emailId && emailId !== 0) {
    throw new Error(MISSING_EMAIL_ID);
  }
};

export const unsubscribe = () =>
  api({
    method: POST,
    url: emailRoutes.UNSUBSCRIBE,
  });

export const reSubscribe = data =>
  api({
    method: GET,
    headers: {},
    withoutAuthorization: true,
    url: emailRoutes.RE_SUBSCRIBE,
    params: data,
  });

export const changePuaseDuration = data =>
  api({
    method: GET,
    headers: {},
    withoutAuthorization: true,
    url: emailRoutes.CHANGE_PAUSE_DURATION,
    params: data,
  });

export const changeUnsubscribeEmailTypePreference = data =>
  api({
    method: GET,
    headers: {},
    withoutAuthorization: true,
    url: urlJoin(
      getEnv('REACT_APP_UNSUBSCRIPTION_BASE_URL'),
      emailRoutes.CHANGE_UNSUBSCRIBE_EMAIL_TYPE_PREFERENCES,
    ),
    params: data,
  });

export const updateReason = data =>
  api({
    method: POST,
    withoutAuthorization: true,
    url: emailRoutes.PAUSE_REASON,
    data,
  });

export const sendSavedTemplate = data =>
  api({
    method: POST,
    url: emailRoutes.SEND_SAVED_TEMPLATE,
    data,
  });

export const sendMail = ([
  data,
  type: SendEmailType = 'regular',
  ,
  isScheduling = false,
]) => {
  const regularRoute = isScheduling
    ? emailRoutes.SCHEDULE_MAIL_SAVED_TEMPLATE
    : emailRoutes.SEND_SAVED_TEMPLATE;
  return api(
    {
      method: POST,
      url: type === 'regular' ? regularRoute : emailRoutes.SEND_CSV_MAIL,
      data,
      headers: {
        'Content-Type':
          type === 'regular' ? 'application/json' : 'multipart/form-data',
      },
    },
    true,
  );
};

export const sendMultiTemplate = data =>
  api({
    method: POST,
    url: emailRoutes.SEND_MULTI_TEMPLATE,
    data,
  });

export const scheduleMailSavedTemplate = data =>
  api({
    method: POST,
    url: emailRoutes.SCHEDULE_MAIL_SAVED_TEMPLATE,
    data,
  });

export const getEmails = () =>
  api({
    method: GET,
    url: emailRoutes.GET_EMAILS,
  });

export const getEmailById = emailId => {
  checkEmailId(emailId);
  return api({
    method: GET,
    url: `${emailRoutes.GET_EMAILS}${emailId}`,
  });
};

export const addEmail = data =>
  api({
    method: POST,
    url: emailRoutes.ADD_EMAIL,
    data,
  });

export const editEmail = data => {
  checkEmailId(data.id);
  return api({
    method: PUT,
    url: `${emailRoutes.EDIT_EMAIL}${data.id}`,
    data,
  });
};

export const deleteEmail = emailId => {
  checkEmailId(emailId);
  return api({
    method: DELETE,
    url: `${emailRoutes.DELETE_EMAIL}${emailId}`,
  });
};

export const getEmailTypes = () =>
  api({
    method: GET,
    url: emailRoutes.EMAIL_TYPES,
  });

export const validateCSVMail = data =>
  mailerServiceApi({
    method: POST,
    url: `${emailRoutes.VALIDATE_CSV}`,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const splitMicrosoftCsv = data =>
  api({
    method: POST,
    url: `${emailRoutes.SPLIT_MICROSOFT_CSV}`,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    isDownload: true,
  });

export const removeInvalidEmailCsv = data =>
  api({
    method: POST,
    url: `${emailRoutes.REMOVE_INVALID_EMAIL_CSV}`,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    isDownload: true,
  });

export const simulateThrottleCSV = data =>
  api(
    {
      method: POST,
      url: emailRoutes.SIMULATE_THROTTLE_CSV,
      data,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      isDownload: true,
    },
    true,
  );

export const saveMxRecordInCsv = data =>
  api({
    method: POST,
    url: emailRoutes.SAVE_MX_RECORD_CSV,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const addToThrottleExemptionsCSV = data => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.ADD_TO_THROTTLE_EXEMPTIONS_CSV,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const addToThrottleExemptions = data => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.ADD_TO_THROTTLE_EXEMPTIONS,
    data: data,
  });
};

export const getAllEmailTypes = () =>
  api({
    method: GET,
    url: emailRoutes.GET_ALL_EMAIL_TYPES,
  });

export const configureEmailTypes = data =>
  api({
    method: POST,
    url: emailRoutes.CONFIGURE_EMAIL_TYPES,
    data,
  });

export const deleteEmailType = id =>
  api({
    method: DELETE,
    url: emailRoutes.DELETE_EMAIL_TYPES(id),
  });

export const deleteBulkThrottleExemptions = ids => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.DELETE_BULK_THROTTLE_EXEMPTIONS,
    data: ids,
  });
};

export const stopCSVMail = async id => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.STOP_CSV_MAIL(id),
  });
};

export const pauseCSVMail = async id => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.PAUSE_CSV_MAIL(id),
  });
};

export const resumeCSVMail = async id => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.RESUME_CSV_MAIL(id),
  });
};

export const triggerCSVMail = async id => {
  return mailerServiceApi({
    method: POST,
    url: emailRoutes.TRIGGER_CSV_MAIL(id),
  });
};

export const watchCSVMail = async id =>
  mailerServiceApi({
    method: POST,
    url: emailRoutes.WATCH_CSV_MAIL(id),
  });

export const unwatchCSVMail = async id =>
  mailerServiceApi({
    method: POST,
    url: emailRoutes.UNWATCH_CSV_MAIL(id),
  });

export const getCSVWatchStatus = async id =>
  mailerServiceApi({
    method: GET,
    url: emailRoutes.GET_CSV_WATCH_STATUS(id),
  });

export const getOneCSVSummary = async id =>
  api({ method: GET, url: emailRoutes.GET_ONE_CSV_SUMMARY(id) });

export const getCsvSignedUrl = id => {
  return api({
    method: GET,
    url: emailRoutes.GET_CSV_SIGNED_URL(id),
  });
};

export const getEmailSendingHistory = filter => {
  return api({
    method: GET,
    url: emailRoutes.GET_EMAIL_SENDING_HISTORY,
    params: filter,
  });
};

export const getEmailDetailsById = id => {
  return api({
    method: GET,
    url: emailRoutes.GET_EMAIL_DETAILS_BY_ID(id),
  });
};

export const getTemplateMetricsByIdAndDateRange = (
  id,
  { startDate, endDate },
) => {
  return api({
    method: GET,
    url: emailRoutes.GET_TEMPLATE_METRICS_BY_ID_AND_DATE_RANGE(id),
    params: {
      startDate,
      endDate,
    },
  });
};

export const getClickEmailsByIdAndDateRange = (
  id,
  { startDate, endDate, limit, cursor },
) => {
  return api({
    method: GET,
    url: emailRoutes.GET_CLICK_EMAILS_BY_ID_AND_DATE_RANGE(id),
    params: {
      startDate,
      endDate,
      limit,
      cursor,
    },
  });
};

export const getThrottleExemptionList = () => {
  return mailerServiceApi({
    method: GET,
    url: emailRoutes.GET_THROTTLE_EXEMPTION_LIST,
  });
};

export const getEmailQueueWatchers = () => {
  return api({
    method: GET,
    url: emailRoutes.GET_EMAIL_QUEUE_WATCHERS,
  });
};

export const watchEmailQueue = () => {
  return api({
    method: POST,
    url: emailRoutes.WATCH_EMAIL_QUEUE,
  });
};

export const unwatchEmailQueue = () => {
  return api({
    method: POST,
    url: emailRoutes.UNWATCH_EMAIL_QUEUE,
  });
};

export const addEmailQueueWatcher = (userId: number) => {
  return api({
    method: POST,
    url: emailRoutes.ADD_EMAIL_QUEUE_WATCHER,
    data: { userId },
  });
};

export const removeEmailQueueWatcher = (userId: number) => {
  return api({
    method: POST,
    url: emailRoutes.REMOVE_EMAIL_QUEUE_WATCHER,
    data: { userId },
  });
};
